import { useEffect, useRef } from 'react';
import Hls from 'hls.js';

interface HlsPlayerProps {
  src: string;
  poster?: string;
  className?: string;
}

export function HlsPlayer({ src, poster, className }: HlsPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    if (Hls.isSupported()) {
      // Extract CloudFront signing params to forward on all sub-requests
      const srcUrl = new URL(src);
      const signingParams = new URLSearchParams();
      for (const key of ['Policy', 'Signature', 'Key-Pair-Id']) {
        const value = srcUrl.searchParams.get(key);
        if (value) signingParams.set(key, value);
      }
      const signingQuery = signingParams.toString();

      const hls = new Hls({
        xhrSetup: (xhr, url) => {
          if (signingQuery && !url.includes('Policy=')) {
            const separator = url.includes('?') ? '&' : '?';
            xhr.open('GET', url + separator + signingQuery, true);
          }
        },
      });
      hlsRef.current = hls;
      hls.loadSource(src);
      hls.attachMedia(video);

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              hls.destroy();
              break;
          }
        }
      });

      return () => {
        hls.destroy();
        hlsRef.current = null;
      };
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      // Safari native HLS support
      video.src = src;
    }
  }, [src]);

  return (
    <video
      ref={videoRef}
      poster={poster}
      controls
      playsInline
      className={className ?? 'w-full rounded-lg'}
    />
  );
}
